<template>
  <div class="min-h-screen bg-white">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 class="text-4xl md:text-5xl font-marketing font-bold text-primary mb-8">Shopping Cart</h1>

      <!-- Loading state -->
      <div v-if="isLoadingCart" class="text-center py-16">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>

      <!-- Empty cart -->
      <div v-else-if="cartItems.length === 0" class="text-center py-16">
        <svg class="w-24 h-24 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
        <p class="text-xl text-gray-600 font-marketing mb-2">Your cart is empty</p>
        <a href="/books" class="text-accent hover:text-green-600 font-marketing underline">Continue Shopping</a>
      </div>

      <!-- Cart items -->
      <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div class="lg:col-span-2">
          <div v-if="unavailableItems.length > 0" class="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p class="text-sm text-yellow-800 font-sans mb-2">
              <strong>Notice:</strong> Some items in your cart are no longer available and have been removed from the
              store. These items will not be included in checkout and can be removed using the remove button below.
            </p>
          </div>
          <div class="space-y-6">
            <div v-for="item in displayItems" :key="`${item.id}-${item.format || 'default'}`" :class="[
              'flex flex-col sm:flex-row gap-4 p-6 border rounded-lg overflow-hidden',
              item.isDiscount ? 'border-green-300 bg-green-50' :
                item.isUnavailable ? 'border-yellow-300 bg-yellow-50 opacity-75' :
                  'border-gray-200 bg-creme-50'
            ]">
              <div v-if="!item.isDiscount" class="flex-shrink-0">
                <a :href="item.bookId ? `/book/${item.bookId}` : `/events/${item.eventId}`" class="block">
                  <div v-if="item.bookId" :style="getBookImageContainerStyle(item)"
                    class="bg-creme-200 rounded-none overflow-hidden shadow-lg">
                    <div v-if="item.coverImage" class="w-full h-full">
                      <img :src="item.coverImage" :alt="item.title" class="w-full h-auto min-h-full object-cover" />
                    </div>
                    <div v-else class="w-full h-full flex items-center justify-center text-gray-400">
                      <svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    </div>
                  </div>
                  <div v-else
                    class="w-24 sm:w-32 bg-gray-200 rounded-none overflow-hidden hover:shadow-lg transition-shadow">
                    <img v-if="item.image" :src="item.image" :alt="item.title" class="w-full h-auto object-cover"
                      style="min-height: 8rem;" />
                    <div v-else class="w-full aspect-[3/4] flex items-center justify-center text-gray-400">
                      <svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>
                </a>
              </div>
              <div v-else class="flex-shrink-0">
                <div class="w-24 sm:w-32 h-24 sm:h-32 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg class="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>

              <div class="flex-1 flex flex-col min-w-0">
                <div class="flex-1 mb-4">
                  <div v-if="!item.isDiscount">
                    <div v-if="item.isUnavailable"
                      class="mb-2 p-2 bg-yellow-100 border border-yellow-300 rounded text-xs text-yellow-800 font-sans">
                      This item is no longer available and has been removed from the store. It will not be included in
                      checkout.
                    </div>
                    <a v-if="!item.isUnavailable"
                      :href="item.bookId ? `/book/${item.bookId}` : `/events/${item.eventId}`" class="block">
                      <h3
                        class="text-lg font-marketing font-semibold text-primary mb-1 hover:text-accent transition-colors"
                        data-no-translate>
                        {{ item.title }}
                      </h3>
                    </a>
                    <h3 v-else class="text-lg font-marketing font-semibold text-gray-500 mb-1" data-no-translate>
                      {{ item.title }}
                    </h3>
                    <p v-if="item.author" class="text-sm text-gray-600 font-sans mb-2" data-no-translate>{{ item.author
                    }}</p>
                    <p v-if="item.location" class="text-sm text-gray-600 font-sans mb-2">{{ item.location }}</p>
                    <p v-if="item.ticketName" class="text-sm text-gray-600 font-sans mb-2">Ticket Holder: {{
                      item.ticketName }}</p>
                    <div v-if="item.format" class="flex items-center gap-2 mb-4">
                      <span :class="[
                        'px-2 py-1 rounded text-xs font-marketing font-semibold',
                        item.format === 'Hardcover'
                          ? 'bg-brown-100 text-brown-800'
                          : 'bg-blue-100 text-blue-800'
                      ]">
                        {{ item.format }}
                      </span>
                    </div>
                    <div v-else-if="item.eventId" class="mb-4">
                      <span class="px-2 py-1 rounded text-xs font-marketing font-semibold bg-accent text-white">
                        Event Ticket
                      </span>
                    </div>
                  </div>
                  <div v-else>
                    <h3 class="text-lg font-marketing font-semibold text-green-700 mb-1" data-no-translate>
                      {{ item.name }}
                    </h3>
                    <p v-if="item.discountDescription" class="text-sm text-gray-600 font-sans italic">
                      {{ item.discountDescription }}
                    </p>
                  </div>
                  <div>
                    <div v-if="getItemDiscountInfo(item)" class="mb-2">
                      <p class="text-sm text-gray-500 font-sans line-through" data-no-translate>
                        Original: ${{ getItemDiscountInfo(item).originalPrice.toFixed(2) }}
                      </p>
                      <div class="flex items-center gap-2">
                        <span
                          class="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-marketing font-semibold">
                          {{ getItemDiscountInfo(item).discountPercentage.toFixed(0) }}% OFF
                        </span>
                        <span class="text-xs text-green-600 font-sans">
                          Save ${{ getItemDiscountInfo(item).savings.toFixed(2) }}
                        </span>
                      </div>
                    </div>
                    <p v-if="getItemPrice(item) !== null" :class="[
                      'text-lg font-marketing font-bold',
                      item.isDiscount ? 'text-green-600' : getItemDiscountInfo(item) ? 'text-green-600' : 'text-primary'
                    ]" data-no-translate>
                      {{ item.isDiscount ? '-' : '' }}${{ getItemPrice(item) }}
                      <span v-if="!item.isDiscount && !item.isEventTicket && item.quantity > 1"
                        class="text-sm text-gray-600 font-normal">
                        × {{ item.quantity }} = ${{ getItemTotal(item).toFixed(2) }}
                      </span>
                    </p>
                    <p v-else class="text-sm text-gray-400 font-sans italic">
                      Loading price...
                    </p>
                  </div>
                </div>

                <div v-if="!item.isDiscount"
                  class="flex flex-col sm:flex-row items-start sm:items-center gap-4 flex-wrap">
                  <div v-if="!item.isUnavailable && !item.isEventTicket && !isDigitalItem(item)"
                    class="flex items-center border border-gray-300 rounded-md flex-shrink-0">
                    <button @click="decrementQuantity(item.id)"
                      class="px-3 py-2 text-primary hover:bg-creme-200 transition-colors font-marketing"
                      aria-label="Decrease quantity">
                      −
                    </button>
                    <span class="px-4 py-2 text-primary font-marketing font-semibold min-w-[3rem] text-center">
                      {{ item.quantity }}
                    </span>
                    <button @click="incrementQuantity(item.id)"
                      class="px-3 py-2 text-primary hover:bg-creme-200 transition-colors font-marketing"
                      aria-label="Increase quantity">
                      +
                    </button>
                  </div>
                  <div v-else-if="isDigitalItem(item)" class="text-sm text-gray-500 font-sans">
                    Digital copy
                  </div>
                  <div v-else-if="item.isUnavailable" class="text-sm text-gray-500 font-sans">
                    Quantity: {{ item.quantity }}
                  </div>

                  <button @click="removeFromCart(item.id)"
                    class="text-red-600 hover:text-red-700 transition-colors font-marketing text-sm underline flex-shrink-0"
                    aria-label="Remove item">
                    Remove
                  </button>
                </div>
                <div v-else class="flex items-center">
                  <button @click="clearOrderDiscount"
                    class="text-red-600 hover:text-red-700 transition-colors font-marketing text-sm underline">
                    Remove discount
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="lg:col-span-1">
          <div class="sticky top-8 bg-creme-50 border border-gray-200 rounded-lg p-6">
            <h2 class="text-2xl font-marketing font-bold text-primary mb-6">Order Summary</h2>

            <div class="space-y-4 mb-6">
              <div v-if="cartTotals.items && cartTotals.items.length > 0"
                class="space-y-2 mb-4 pb-4 border-b border-gray-200">
                <div v-for="item in cartTotals.items.filter(i => i.book_id || i.event_id)"
                  :key="`${item.book_id || item.event_id}-${item.name}-${item.book_format_id || 'default'}`"
                  class="text-xs font-sans">
                  <div class="flex justify-between items-start">
                    <div class="flex-1 min-w-0 pr-2">
                      <div class="text-gray-700 font-semibold truncate" data-no-translate>{{ item.name }}</div>
                      <div v-if="item.quantity > 1" class="text-gray-500 text-xs mt-0.5">
                        {{ item.quantity }} × ${{ item.unit_price.toFixed(2) }}
                      </div>
                      <div
                        v-if="item.original_unit_price && item.discount_percentage && item.original_unit_price > item.unit_price && item.discount_percentage > 0"
                        class="text-green-600 mt-1">
                        <span class="line-through text-gray-400" data-no-translate>${{
                          item.original_unit_price.toFixed(2) }}</span>
                        <span class="ml-1">{{ item.discount_percentage.toFixed(0) }}% off</span>
                        <span v-if="item.quantity === 1" class="ml-1 text-xs">(-${{ (item.original_unit_price -
                          item.unit_price).toFixed(2) }})</span>
                        <span v-else class="ml-1 text-xs">(-${{ ((item.original_unit_price - item.unit_price) *
                          item.quantity).toFixed(2) }} total)</span>
                      </div>
                    </div>
                    <div class="text-right">
                      <div class="text-gray-900 font-semibold" data-no-translate>${{ item.item_total.toFixed(2) }}</div>
                      <div v-if="item.discount_amount > 0" class="text-green-600 text-xs mt-0.5" data-no-translate>-${{
                        item.discount_amount.toFixed(2) }} discount</div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex justify-between text-sm font-sans">
                <span class="text-gray-600">Subtotal</span>
                <span class="text-primary font-semibold" data-no-translate>${{ subtotal.toFixed(2) }}</span>
              </div>

              <div v-if="orderDiscountCode && discountAmount > 0" class="space-y-1">
                <div class="flex justify-between text-sm font-sans">
                  <span class="text-gray-600">Discount (<span data-no-translate>{{ orderDiscountCode }}</span>)</span>
                  <span class="text-green-600 font-semibold" data-no-translate>-${{ discountAmount.toFixed(2) }}</span>
                </div>
                <p v-if="discountDescription" class="text-xs text-gray-500 font-sans italic">
                  {{ discountDescription }}
                </p>
              </div>

              <div class="border-t border-gray-300 pt-4 flex justify-between text-lg font-marketing font-bold">
                <span class="text-primary">Total</span>
                <span class="text-primary" data-no-translate>${{ total.toFixed(2) }}</span>
              </div>
            </div>

            <div class="mb-6 border-t border-gray-300 pt-4">
              <div v-if="!orderDiscountCode" class="space-y-2">
                <input id="order-discount-code" v-model="orderDiscountInput" type="text" placeholder="Discount code"
                  class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
                <p v-if="orderDiscountError" class="text-xs text-red-600 font-sans">
                  {{ orderDiscountError }}
                </p>
              </div>
              <div v-else class="space-y-2">
                <div class="flex items-center justify-between">
                  <span class="text-sm font-sans text-gray-600">
                    Discount code: <span class="font-semibold text-primary" data-no-translate>{{ orderDiscountCode
                    }}</span>
                  </span>
                  <button @click="clearOrderDiscount"
                    class="text-xs text-red-600 hover:text-red-700 font-marketing underline">
                    Remove
                  </button>
                </div>
              </div>
            </div>


            <button @click="proceedToCheckout" :disabled="availableItems.length === 0 || processingCheckout" :class="[
              'w-full bg-accent text-white px-6 py-3 rounded-md hover:bg-green-600 transition-colors font-marketing text-lg font-semibold mb-4 disabled:opacity-50 disabled:cursor-not-allowed',
              (availableItems.length === 0 || processingCheckout) ? 'opacity-50 cursor-not-allowed' : ''
            ]">
              {{ processingCheckout ? 'Processing...' : 'Proceed to Checkout' }}
            </button>

            <a href="/books"
              class="block text-center text-accent hover:text-green-600 font-marketing underline text-sm">
              Continue Shopping
            </a>
          </div>
        </div>
      </div>
    </div>


    <div v-if="receipt" class="mt-8 bg-white border border-gray-200 rounded-lg p-6">
      <h2 class="text-2xl font-marketing font-bold text-primary mb-4">Receipt</h2>
      <div class="space-y-4">
        <div class="flex justify-between">
          <span class="text-gray-600 font-sans">Order Number:</span>
          <span class="font-marketing font-semibold text-primary">{{ receipt.order_number }}</span>
        </div>
        <div class="flex justify-between">
          <span class="text-gray-600 font-sans">Status:</span>
          <span :class="[
            'font-marketing font-semibold',
            receipt.fulfilled ? 'text-green-600' : 'text-yellow-600'
          ]">
            {{ receipt.fulfilled ? 'Fulfilled' : 'Pending Fulfillment' }}
          </span>
        </div>
        <div class="flex justify-between">
          <span class="text-gray-600 font-sans">Payment Status:</span>
          <span class="font-marketing font-semibold text-primary">{{ receipt.payment_status }}</span>
        </div>
        <div class="flex justify-between">
          <span class="text-gray-600 font-sans">Total:</span>
          <span class="font-marketing font-bold text-primary">${{ parseFloat(receipt.total_amount).toFixed(2) }}</span>
        </div>
        <div v-if="!receipt.fulfilled" class="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <p class="text-sm text-yellow-800 font-sans">
            Your payment has been received. We are processing your order and will update this receipt shortly.
            <button @click="checkReceiptStatus" class="ml-2 text-yellow-900 underline font-semibold">
              Refresh
            </button>
          </p>
        </div>
        <div v-else class="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
          <p class="text-sm text-green-800 font-sans">
            Your order has been fulfilled! Thank you for your purchase.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { cartService } from '../services/cart'
import { authService } from '../services/auth'
import { paymentService } from '../services/payment'
import { useBookImageStyle } from '../composables/useBookImageStyle'

const { getBookImageContainerStyle: getBookImageContainerStyleUtil } = useBookImageStyle()

const cartItems = cartService.cartItems
const subtotal = cartService.subtotal
const total = cartService.total
const discountAmount = cartService.discountAmount
const getItemTotal = cartService.getItemTotal
const orderDiscountCode = cartService.orderDiscountCode
const discountDescription = computed(() => cartService.cartTotals.value.discount_description)
const cartTotals = cartService.cartTotals

// Loading state
const isLoadingCart = ref(true)

const displayItems = computed(() => {
  const items = []

  cartItems.value.forEach(item => {
    if (item.eventId && item.ticketNames && item.ticketNames.length > 0) {
      item.ticketNames.forEach((ticketName, index) => {
        items.push({
          ...item,
          id: `${item.id}-ticket-${index}`,
          title: `${item.title} - ${ticketName}`,
          quantity: 1,
          ticketName: ticketName,
          isEventTicket: true
        })
      })
    } else {
      items.push(item)
    }
  })

  const discountItem = cartService.cartTotals.value.items.find(
    item => item.book_id === null && item.event_id === null && item.discount_code
  )
  if (discountItem) {
    items.push({
      id: 'discount-item',
      isDiscount: true,
      name: discountItem.name,
      discountCode: discountItem.discount_code,
      discountDescription: discountDescription.value,
      price: discountItem.unit_price,
      quantity: 1
    })
  }
  return items
})

const unavailableItems = computed(() => {
  return cartItems.value.filter(item => item.isUnavailable)
})

const availableItems = computed(() => {
  return cartItems.value.filter(item => !item.isUnavailable)
})

const orderDiscountInput = ref('')
const orderDiscountError = ref(null)
const currentUser = ref(null)
const receipt = ref(null)
const paymentIntentId = ref(null)
const processingCheckout = ref(false)
let pollInterval = null

onMounted(async () => {
  isLoadingCart.value = true

  currentUser.value = authService.getStoredUser()

  if (!currentUser.value && authService.isAuthenticated()) {
    try {
      currentUser.value = await authService.getCurrentUser()
    } catch (error) {
      console.error('Failed to refresh user:', error)
    }
  }

  const currentUserId = currentUser.value?.id || null

  const loadResult = await cartService.loadCart(currentUserId)

  if (loadResult && loadResult.removedItems && loadResult.removedItems.length > 0) {
    const itemTitles = loadResult.removedItems.map(item => item.title).join(', ')
    const itemType = loadResult.removedItems[0].type === 'book' ? 'item' : 'event'
    const message = loadResult.removedItems.length === 1
      ? `The ${itemType} "${itemTitles}" is no longer available and has been removed from your cart. It may have been removed from the store and will not be included in checkout.`
      : `${loadResult.removedItems.length} items are no longer available and have been removed from your cart. They may have been removed from the store and will not be included in checkout.`

    setTimeout(() => {
      alert(message)
    }, 100)
  }

  // Cart is already synced in loadCart, no need to sync again

  await cartService.calculateCartTotals(currentUserId)

  // Set loading to false after all data is loaded
  isLoadingCart.value = false
})

onUnmounted(() => {
  if (pollInterval) {
    clearInterval(pollInterval)
  }
})

// E-books / audiobooks are single-copy digital goods, so no quantity stepper.
const isDigitalItem = (item) =>
  ['ebook', 'audiobook'].includes(String(item.format || '').toLowerCase())

const incrementQuantity = async (itemId) => {
  await cartService.incrementQuantity(itemId, currentUser.value?.id)
}

const decrementQuantity = async (itemId) => {
  await cartService.decrementQuantity(itemId, currentUser.value?.id)
}

const removeFromCart = async (itemId) => {
  if (itemId.includes('-ticket-')) {
    const originalItemId = itemId.split('-ticket-')[0]
    const ticketIndex = parseInt(itemId.split('-ticket-')[1])
    const originalItem = cartItems.value.find(item => item.id === originalItemId)

    if (originalItem && originalItem.ticketNames && originalItem.ticketNames.length > ticketIndex) {
      originalItem.ticketNames.splice(ticketIndex, 1)
      originalItem.quantity = originalItem.ticketNames.length

      if (originalItem.quantity === 0) {
        await cartService.removeFromCart(originalItemId, currentUser.value?.id)
      } else {
        const cartData = {
          items: cartItems.value,
          orderDiscountCode: orderDiscountCode.value,
          orderDiscountInfo: null
        }
        const encoded = encodeURIComponent(JSON.stringify(cartData))
        const CART_COOKIE_KEY = 'barry_bonna_cart'
        const CART_STORAGE_KEY = 'barry_bonna_cart'

        if (encoded.length > 4000) {
          localStorage.setItem(CART_STORAGE_KEY, encoded)
          document.cookie = `${CART_COOKIE_KEY}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`
        } else {
          document.cookie = `${CART_COOKIE_KEY}=${encoded}; path=/; max-age=${365 * 24 * 60 * 60}`
          localStorage.removeItem(CART_STORAGE_KEY)
        }
        await cartService.calculateCartTotals(currentUser.value?.id)
      }
    }
  } else {
    await cartService.removeFromCart(itemId, currentUser.value?.id)
    await cartService.calculateCartTotals(currentUser.value?.id)
  }
}

const proceedToCheckout = async () => {
  if (availableItems.value.length === 0 || processingCheckout.value) {
    return
  }

  processingCheckout.value = true
  orderDiscountError.value = null

  try {
    const code = orderDiscountInput.value?.trim()
    const userId = currentUser.value?.id

    if (code && !orderDiscountCode.value) {
      const result = await cartService.applyOrderDiscountCode(code, userId)

      if (!result.success) {
        console.error('Discount validation failed:', result.message)
        orderDiscountError.value = result.message
        processingCheckout.value = false
        return
      }
    }

    if (!authService.isAuthenticated()) {
      window.location.href = '/checkout-auth'
    } else {
      window.location.href = '/checkout'
    }
  } catch (error) {
    console.error('Failed to proceed to checkout:', error)
    orderDiscountError.value = 'An error occurred while processing your request. Please try again.'
    processingCheckout.value = false
  }
}

const clearOrderDiscount = async () => {
  await cartService.clearOrderDiscount(currentUser.value?.id)
  orderDiscountInput.value = ''
  orderDiscountError.value = null
}


const checkReceiptStatus = async () => {
  if (!paymentIntentId.value) return

  try {
    const receiptData = await paymentService.getReceiptByPaymentIntent(paymentIntentId.value)
    receipt.value = receiptData

    if (receiptData.fulfilled && pollInterval) {
      clearInterval(pollInterval)
      pollInterval = null
    }
  } catch (error) {
    console.error('Failed to check receipt status:', error)
  }
}

const getBookImageContainerStyle = (item) => {
  return getBookImageContainerStyleUtil(item)
}

const getItemDiscountInfo = (item) => {
  if (item.isDiscount || item.isUnavailable) return null

  const backendItem = cartTotals.value.items.find(backendItem => {
    if (item.bookId) {
      if (backendItem.book_id !== item.bookId) return false

      if (
        item.bookFormatId !== null &&
        item.bookFormatId !== undefined &&
        backendItem.book_format_id !== null &&
        backendItem.book_format_id !== undefined
      ) {
        return backendItem.book_format_id === item.bookFormatId
      }

      if (item.format && backendItem.name) {
        return backendItem.name.includes(`(${item.format})`)
      }

      return !item.format && !backendItem.name.includes('(')
    }
    if (item.eventId) {
      return backendItem.event_id === item.eventId
    }
    return false
  })

  if (!backendItem) return null

  if (
    backendItem.original_unit_price &&
    backendItem.discount_percentage &&
    backendItem.original_unit_price > backendItem.unit_price &&
    backendItem.discount_percentage > 0
  ) {
    return {
      originalPrice: backendItem.original_unit_price,
      discountedPrice: backendItem.unit_price,
      discountPercentage: backendItem.discount_percentage,
      savings: backendItem.original_unit_price - backendItem.unit_price
    }
  }

  return null
}

const getItemPrice = (item) => {
  const discountInfo = getItemDiscountInfo(item)
  const price = discountInfo ? discountInfo.discountedPrice : item.price

  // Return null if price is not a valid number
  if (price === null || price === undefined || isNaN(price)) {
    return null
  }

  return Math.abs(price).toFixed(2)
}
</script>

<style scoped></style>
